// Telemetry CSV (IMPLEMENTATION §2.9 "Telemetry"): columns `wall_ms, session, tick, event, subject, value,
// extra`, RFC 4180 escaping (a field containing a comma, a double quote, CR or LF is quoted and its quotes
// doubled), CRLF line ends, header first. PURE — shared by the app (ring + export), tools/telemetry-report.ts
// and the tests.

export const TELEMETRY_COLUMNS = [
  'wall_ms',
  'session',
  'tick',
  'event',
  'subject',
  'value',
  'extra',
] as const;

export const CSV_HEADER = TELEMETRY_COLUMNS.join(',');
export const CSV_EOL = '\r\n';

export interface TelemetryRecord {
  wall_ms: number;
  session: number;
  tick: number;
  event: string;
  subject: string;
  value: number;
  extra: string;
}

/** One CSV field: quoted when it contains `,` `"` CR or LF (inner quotes doubled). */
export function csvField(v: string | number): string {
  const s = typeof v === 'number' ? (Number.isFinite(v) ? String(v) : '') : v;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** One data line (no line end). */
export function csvLine(r: TelemetryRecord): string {
  return [r.wall_ms, r.session, r.tick, r.event, r.subject, r.value, r.extra]
    .map(csvField)
    .join(',');
}

/** The whole file: header + one line per row, CRLF-terminated. */
export function toCsv(rows: readonly TelemetryRecord[]): string {
  return linesToCsv(rows.map(csvLine));
}

/** Header + pre-escaped lines (the ring keeps each row's line so a flush is one join). */
export function linesToCsv(lines: readonly string[]): string {
  return `${CSV_HEADER}${CSV_EOL}${lines.map((l) => `${l}${CSV_EOL}`).join('')}`;
}

/**
 * RFC 4180 parser: quoted fields may hold commas, doubled quotes and line breaks; CRLF or LF line ends; a
 * trailing line end does not make an empty record. Throws on a quote inside an unquoted field or an
 * unterminated quoted field (a truncated file).
 */
export function parseCsv(text: string): string[][] {
  const out: string[][] = [];
  let row: string[] = [];
  let field = '';
  let i = 0;
  let quoted = false;
  /** At the first character of a field (row start or right after a comma): only there may a quote open. */
  let atStart = true;
  /** The current record has content (a character, a comma or a quote), so the end of input closes it. */
  let pending = false;
  while (i < text.length) {
    const ch = text[i] as string;
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        quoted = false;
        i += 1;
        const next = text[i];
        if (next !== undefined && next !== ',' && next !== '\r' && next !== '\n')
          throw new Error(`csv: text after a closing quote at ${i}`);
        continue;
      }
      field += ch;
      i += 1;
      continue;
    }
    if (ch === '"') {
      if (!atStart) throw new Error(`csv: stray quote at ${i}`);
      quoted = true;
      atStart = false;
      pending = true;
      i += 1;
    } else if (ch === ',') {
      row.push(field);
      field = '';
      atStart = true;
      pending = true;
      i += 1;
    } else if (ch === '\r' || ch === '\n') {
      row.push(field);
      out.push(row);
      row = [];
      field = '';
      atStart = true;
      pending = false;
      i += ch === '\r' && text[i + 1] === '\n' ? 2 : 1;
    } else {
      field += ch;
      atStart = false;
      pending = true;
      i += 1;
    }
  }
  if (quoted) throw new Error('csv: unterminated quoted field');
  if (pending) {
    row.push(field);
    out.push(row);
  }
  return out;
}

const INT = /^-?\d+$/;
const NUM = /^-?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][-+]?\d+)?$/;

/**
 * Parses a telemetry CSV (header required, exactly the 7 columns in order) into typed records. `wall_ms`,
 * `session` and `tick` must be integers and `value` a number; `event` must be non-empty. Throws with the
 * line number on the first bad row — or, with `onBadRow`, reports it there and skips it (the app's stored
 * ring: one bad row must not cost the other 4,999). A bad header or broken quoting always throws.
 */
export function parseTelemetryCsv(
  text: string,
  onBadRow?: (line: number, why: string) => void,
): TelemetryRecord[] {
  const rows = parseCsv(text);
  const head = rows[0];
  if (!head || head.join(',') !== CSV_HEADER)
    throw new Error(`telemetry csv: header must be "${CSV_HEADER}"`);
  const out: TelemetryRecord[] = [];
  for (let n = 1; n < rows.length; n++) {
    try {
      out.push(parseRecord(rows[n] as string[], n + 1));
    } catch (err) {
      if (!onBadRow) throw err;
      onBadRow(n + 1, err instanceof Error ? err.message : String(err));
    }
  }
  return out;
}

function parseRecord(r: readonly string[], line: number): TelemetryRecord {
  if (r.length !== TELEMETRY_COLUMNS.length)
    throw new Error(`telemetry csv: line ${line} has ${r.length} fields, not 7`);
  const [wall, session, tick, event, subject, value, extra] = r as [
    string,
    string,
    string,
    string,
    string,
    string,
    string,
  ];
  if (!INT.test(wall) || !INT.test(session) || !INT.test(tick))
    throw new Error(`telemetry csv: line ${line}: wall_ms / session / tick must be integers`);
  if (!NUM.test(value)) throw new Error(`telemetry csv: line ${line}: value must be a number`);
  if (event.length === 0) throw new Error(`telemetry csv: line ${line}: empty event`);
  return {
    wall_ms: Number(wall),
    session: Number(session),
    tick: Number(tick),
    event,
    subject,
    value: Number(value),
    extra,
  };
}
